import { allSupportedServerErrors, ServerError, ServerErrorShape, ServerErrorType } from '../types';

/**
 * Selects the error from the response if it exists.
 *
 * Since it can be in multiple places, we are checking all of them.
 */
export function extractErrorTypeFromResponse(response: unknown): ServerErrorType | null {
  if (typeof response !== 'object') {
    return null;
  }

  if (response === null) {
    return null;
  }

  if ('error' in response && typeof response?.error === 'string') {
    if (isValidServerError(response.error)) {
      return response.error;
    }

    // If the error is not known, we return a generic error.
    return 'bad_request';
  }

  if (
    'error' in response &&
    typeof response.error === 'object' &&
    response.error !== null &&
    'type' in response.error &&
    typeof response?.error?.type === 'string'
  ) {
    if (isValidServerError(response.error.type)) {
      return response.error.type;
    }

    // If the error is not known, we return a generic error.
    return 'bad_request';
  }

  return null;
}

/**
 * Type narrowing function which checks if the server error is a valid type.
 *
 * Will change the type on the source `response` parameter where it's used to be `ServerErrorType`.
 */
function isValidServerError(
  response: string | ServerError | ServerErrorType | ServerErrorShape
): response is ServerErrorType {
  if (typeof response === 'string') {
    return allSupportedServerErrors.includes(response as ServerErrorType);
  }

  if (typeof response !== 'object') {
    return false;
  }

  if (response === null) {
    return false;
  }

  if ('error' in response && typeof response?.error === 'string') {
    return allSupportedServerErrors.includes(response.error as ServerErrorType);
  }

  if (
    'error' in response &&
    typeof response?.error === 'object' &&
    response.error !== null &&
    'type' in response.error &&
    'type' in response.error
  ) {
    return allSupportedServerErrors.includes(response.error.type as ServerErrorType);
  }

  return false;
}
